/**
 * QSignalList - Compact Symbol Selection List
 *
 * Option A Layout: LEFT column (25%) symbol selector
 * Shows all available symbols in a compact, selectable list format.
 * No expanded analytics - just selection interface.
 *
 * Features:
 * - Compact symbol cards with signal status
 * - Category filtering (All, Forex, Metals, Crypto)
 * - Click-to-select behavior
 * - Visual highlight for selected symbol
 * - All available symbols from Signal Board + QuantX defaults
 *
 * @version 1.0.0
 * @phase Phase 4 - Option A Layout Consolidation
 */

import React, { memo, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Chip, TextField, InputAdornment, Tooltip, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getDisplaySymbol, getCompactSymbol, pairToQuantX, symbolsMatch } from '../../utils/symbolMapper';
import {
    COLORS,
    SPACING,
    RADIUS,
    getSignalColor,
    SHARED_STYLES
} from './signalBoardTheme';
import { QAI_COLORS } from '../../theme/qaiDesignTokens';  // P2-3: Unified branding

/**
 * Categorize symbol by type
 */
const categorizeSymbol = (symbol) => {
    if (!symbol) return 'forex';
    const upper = symbol.toUpperCase().trim();
    if (upper.includes('/')) return 'pairs';
    if (upper.includes('XAU') || upper.includes('XAG') || upper === 'GOLD' || upper === 'SILVER') return 'metals';
    if (upper.includes('US30') || upper.includes('US500') || upper.includes('NAS') || upper.includes('DAX')) return 'indices';
    if (upper.includes('BTC') || upper.includes('ETH') || upper.includes('SOL')) return 'crypto';
    return 'forex';
};

/**
 * Category definitions
 */
const CATEGORIES = [
    { id: 'all', label: 'All' },
    { id: 'metals', label: 'Metals' },
    { id: 'forex', label: 'Forex' },
    { id: 'crypto', label: 'Crypto' }
];

/**
 * Get signal verdict from signal data
 */
const getSignalVerdict = (signal) => {
    const { qSignal, qScore, zSignal, trendScore } = signal || {};
    const score = qScore ?? trendScore ?? 50;

    if (qSignal) {
        const upper = qSignal.toUpperCase();
        if (upper === 'HOLD' || upper === 'WAIT' || upper === 'NEUTRAL') {
            return { action: 'HOLD', type: 'neutral', score };
        }
        if (upper.includes('BUY')) return { action: 'BUY', type: 'buy', score };
        if (upper.includes('SELL')) return { action: 'SELL', type: 'sell', score };
    }

    return { action: 'HOLD', type: 'neutral', score };
};

/**
 * Generate contextual Ask Q prompt for a symbol
 * @param {object} signal - Signal data with symbol, qSignal, qScore
 * @returns {string} - Formatted prompt for Q AI
 */
const generateAskQPrompt = (signal) => {
    const displayName = getDisplaySymbol(signal.symbol);
    const verdict = getSignalVerdict(signal);

    // Build contextual prompt based on signal type
    if (verdict.type === 'buy') {
        return `Analyze ${displayName} - showing ${verdict.action} signal (${verdict.score}%). What are the key levels and entry strategy?`;
    } else if (verdict.type === 'sell') {
        return `Analyze ${displayName} - showing ${verdict.action} signal (${verdict.score}%). What are the risks and exit strategy?`;
    } else {
        return `Tell me about ${displayName} - current market conditions, key levels, and what to watch for.`;
    }
};

/**
 * Compact Signal Item - Single symbol row with Ask Q button
 */
const SignalItem = memo(({ signal, isSelected, onClick, onAskQ }) => {
    const verdict = getSignalVerdict(signal);
    const statusColor = verdict.type === 'buy'
        ? COLORS.signalBuy
        : verdict.type === 'sell'
            ? COLORS.signalSell
            : COLORS.signalNeutral;

    const TrendIcon = verdict.type === 'buy' ? TrendingUpIcon :
        verdict.type === 'sell' ? TrendingDownIcon : TrendingFlatIcon;

    // Use utility for clean symbol display
    const displaySymbol = getCompactSymbol(signal.symbol);

    // Handle Ask Q click - prevent propagation to avoid selecting symbol
    const handleAskQ = useCallback((e) => {
        e.stopPropagation();
        if (onAskQ) {
            const prompt = generateAskQPrompt(signal);
            onAskQ(prompt);
        }
    }, [signal, onAskQ]);

    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '6px 8px',
                backgroundColor: isSelected ? 'rgba(39, 181, 109, 0.12)' : 'transparent',
                borderLeft: isSelected ? '3px solid #27B56D' : '3px solid transparent',
                borderBottom: '1px solid rgba(55, 65, 81, 0.2)',
                cursor: 'pointer',
                transition: 'all 0.12s ease',
                '&:hover': {
                    backgroundColor: isSelected ? 'rgba(39, 181, 109, 0.15)' : 'rgba(55, 65, 81, 0.15)',
                    '& .ask-q-btn': {
                        opacity: 1,
                        transform: 'scale(1)'
                    }
                }
            }}
        >
            {/* Left: Icon + Symbol */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
                <TrendIcon sx={{ fontSize: 13, color: statusColor, flexShrink: 0 }} />
                <Typography
                    sx={{
                        fontSize: '10px',
                        fontWeight: isSelected ? 700 : 600,
                        color: isSelected ? '#fff' : COLORS.textPrimary,
                        fontFamily: "'JetBrains Mono', monospace",
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}
                >
                    {displaySymbol}
                </Typography>
            </Box>

            {/* Right: Signal Badge + Score + Ask Q Button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                {/* Signal Badge */}
                <Box
                    sx={{
                        backgroundColor: `${statusColor}20`,
                        color: statusColor,
                        fontSize: '10px',
                        fontWeight: 700,
                        padding: '2px 5px',
                        borderRadius: '3px',
                        fontFamily: "'JetBrains Mono', monospace"
                    }}
                >
                    {verdict.action}
                </Box>

                {/* Score */}
                <Typography
                    sx={{
                        fontSize: '10px',
                        fontWeight: 700,
                        color: statusColor,
                        fontFamily: "'JetBrains Mono', monospace",
                        minWidth: '28px',
                        textAlign: 'right'
                    }}
                >
                    {verdict.score}%
                </Typography>

                {/* Ask Q Button - Shows on hover or when selected */}
                {onAskQ && (
                    <Tooltip title="Ask Q about this symbol" placement="left">
                        <IconButton
                            className="ask-q-btn"
                            onClick={handleAskQ}
                            size="small"
                            sx={{
                                width: 22,
                                height: 22,
                                opacity: isSelected ? 1 : 0,
                                transform: isSelected ? 'scale(1)' : 'scale(0.8)',
                                transition: 'all 0.15s ease',
                                backgroundColor: 'rgba(124, 58, 237, 0.15)',
                                border: '1px solid rgba(124, 58, 237, 0.3)',
                                color: '#a855f7',
                                '&:hover': {
                                    backgroundColor: 'rgba(124, 58, 237, 0.25)',
                                    borderColor: 'rgba(124, 58, 237, 0.5)'
                                }
                            }}
                        >
                            <AutoAwesomeIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
});
SignalItem.displayName = 'SignalItem';

/**
 * QSignalList Component
 */
const QSignalList = memo(({
    signals = [],
    selectedSymbol,
    onSymbolSelect,
    onAskQ,
    availableSymbols = [],
    showSearch = true,
    showCategories = true
}) => {
    const [activeCategory, setActiveCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Merge signals with available symbols - DEDUP using QuantX mapping
    // Signal Board sends cross-pairs (GOLD/SILVER), availableSymbols has QuantX (XAUUSD_ct)
    // We need to avoid showing both GOLD/SILVER AND XAUUSD_ct
    const allSymbols = useMemo(() => {
        const signalMap = new Map();
        const coveredQuantX = new Set(); // Track which QuantX symbols are already covered

        // Add signals first (they have score/verdict data)
        signals.forEach(s => {
            signalMap.set(s.symbol, s);
            // Track the QuantX symbol this signal maps to
            const quantx = pairToQuantX(s.symbol);
            if (quantx) {
                coveredQuantX.add(quantx);
            }
        });

        // Add available symbols that aren't already covered by signals
        // This prevents XAUUSD_ct from appearing when GOLD/SILVER already exists
        availableSymbols.forEach(sym => {
            const quantx = pairToQuantX(sym);
            // Only add if:
            // 1. This exact symbol isn't already in signals
            // 2. AND its QuantX equivalent isn't already covered
            if (!signalMap.has(sym) && !coveredQuantX.has(quantx)) {
                signalMap.set(sym, {
                    symbol: sym,
                    qSignal: 'NEUTRAL',
                    qScore: null,
                    _noSignalData: true
                });
                coveredQuantX.add(quantx);
            }
        });

        console.log('[QSignalList] Symbol dedup:', {
            signalCount: signals.length,
            availableCount: availableSymbols.length,
            mergedCount: signalMap.size,
            coveredQuantX: Array.from(coveredQuantX)
        });

        return Array.from(signalMap.values());
    }, [signals, availableSymbols]);

    // Calculate category counts
    const categoryCounts = useMemo(() => {
        const counts = { all: 0, forex: 0, metals: 0, crypto: 0, indices: 0, pairs: 0 };
        allSymbols.forEach(s => {
            counts.all++;
            const cat = categorizeSymbol(s.symbol);
            if (counts[cat] !== undefined) {
                counts[cat]++;
            }
        });
        return counts;
    }, [allSymbols]);

    // Filter symbols by category and search
    const filteredSymbols = useMemo(() => {
        let filtered = allSymbols;

        // Category filter
        if (activeCategory !== 'all') {
            filtered = filtered.filter(s => categorizeSymbol(s.symbol) === activeCategory);
        }

        // Search filter
        if (searchTerm) {
            const term = searchTerm.toUpperCase();
            filtered = filtered.filter(s =>
                s.symbol.toUpperCase().includes(term)
            );
        }

        // Sort: selected first, then by score
        filtered.sort((a, b) => {
            if (a.symbol === selectedSymbol) return -1;
            if (b.symbol === selectedSymbol) return 1;
            return (b.qScore || 0) - (a.qScore || 0);
        });

        return filtered;
    }, [allSymbols, activeCategory, searchTerm, selectedSymbol]);

    // Handle symbol selection
    const handleSelect = useCallback((symbol) => {
        if (onSymbolSelect) {
            onSymbolSelect(symbol);
        }
    }, [onSymbolSelect]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                backgroundColor: '#0a0a0a',
                overflow: 'hidden'
            }}
        >
            {/* Header - P2-3: Unified cyan branding */}
            <Box
                sx={{
                    padding: '10px 12px',
                    borderBottom: '1px solid rgba(55, 65, 81, 0.3)',
                    backgroundColor: QAI_COLORS.brand.primaryMuted  // Cyan muted bg
                }}
            >
                <Typography
                    sx={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: QAI_COLORS.brand.primary,  // Cyan brand color
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase'
                    }}
                >
                    Signal Board
                </Typography>
                <Typography
                    sx={{
                        fontSize: '10px',
                        color: COLORS.textMuted,
                        marginTop: '2px'
                    }}
                >
                    {filteredSymbols.length} symbols available
                </Typography>
            </Box>

            {/* Search - COMPACT per UI/UX spec */}
            {showSearch && (
                <Box sx={{ padding: '6px 8px', borderBottom: '1px solid rgba(55, 65, 81, 0.2)' }}>
                    <TextField
                        size="small"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 14, color: COLORS.textMuted }} />
                                </InputAdornment>
                            )
                        }}
                        sx={{
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(55, 65, 81, 0.2)',
                                fontSize: '10px',  // REDUCED from 11px
                                height: '28px',    // REDUCED from 32px
                                '& fieldset': { borderColor: 'rgba(55, 65, 81, 0.3)' },
                                '&:hover fieldset': { borderColor: 'rgba(55, 65, 81, 0.5)' },
                                '&.Mui-focused fieldset': { borderColor: '#27B56D' }
                            },
                            '& .MuiInputBase-input': {
                                color: COLORS.textPrimary,
                                padding: '4px 6px',  // REDUCED from 6px 8px
                                '&::placeholder': { color: COLORS.textMuted, opacity: 1 }
                            }
                        }}
                    />
                </Box>
            )}

            {/* Categories - COMPACT per UI/UX spec */}
            {showCategories && (
                <Box
                    sx={{
                        display: 'flex',
                        gap: '3px',  // REDUCED from 4px
                        padding: '6px 8px',  // REDUCED from 8px 10px
                        borderBottom: '1px solid rgba(55, 65, 81, 0.3)',
                        flexWrap: 'wrap'
                    }}
                >
                    {CATEGORIES.filter(cat => categoryCounts[cat.id] > 0 || cat.id === 'all').map(cat => (
                        <Chip
                            key={cat.id}
                            label={`${cat.label}${categoryCounts[cat.id] > 0 ? ` ${categoryCounts[cat.id]}` : ''}`}
                            size="small"
                            onClick={() => setActiveCategory(cat.id)}
                            sx={{
                                backgroundColor: activeCategory === cat.id
                                    ? 'rgba(39, 181, 109, 0.2)'
                                    : 'transparent',
                                color: activeCategory === cat.id
                                    ? '#27B56D'
                                    : COLORS.textMuted,
                                border: `1px solid ${activeCategory === cat.id
                                    ? 'rgba(39, 181, 109, 0.4)'
                                    : 'transparent'}`,
                                cursor: 'pointer',
                                fontSize: '10px',  // PHASE 1.1: Minimum 10px
                                height: '20px',   // REDUCED from 24px
                                fontWeight: activeCategory === cat.id ? 600 : 500,
                                '&:hover': {
                                    backgroundColor: activeCategory === cat.id
                                        ? 'rgba(39, 181, 109, 0.25)'
                                        : 'rgba(55, 65, 81, 0.2)'
                                },
                                '& .MuiChip-label': { padding: '0 6px' }  // REDUCED from 8px
                            }}
                        />
                    ))}
                </Box>
            )}

            {/* Symbol List */}
            <Box
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    ...SHARED_STYLES.scrollbar
                }}
            >
                {filteredSymbols.length === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100px',
                            padding: SPACING.md
                        }}
                    >
                        <Typography sx={{ fontSize: '11px', color: COLORS.textMuted }}>
                            {searchTerm ? 'No matching symbols' : 'No signals available'}
                        </Typography>
                    </Box>
                ) : (
                    filteredSymbols.map((signal) => (
                        <SignalItem
                            key={signal.symbol}
                            signal={signal}
                            isSelected={signal.symbol === selectedSymbol}
                            onClick={() => handleSelect(signal.symbol)}
                            onAskQ={onAskQ}
                        />
                    ))
                )}
            </Box>

{/* Footer removed per UI/UX spec - saves ~24px vertical space */}
        </Box>
    );
});

QSignalList.displayName = 'QSignalList';

QSignalList.propTypes = {
    signals: PropTypes.arrayOf(PropTypes.shape({
        symbol: PropTypes.string.isRequired,
        qSignal: PropTypes.string,
        qScore: PropTypes.number
    })),
    selectedSymbol: PropTypes.string,
    onSymbolSelect: PropTypes.func,
    onAskQ: PropTypes.func,
    availableSymbols: PropTypes.arrayOf(PropTypes.string),
    showSearch: PropTypes.bool,
    showCategories: PropTypes.bool
};

export default QSignalList;
